import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { FAITHS, ZODIAC, BODY_TYPES } from "@/lib/lunelle-data";
import { previewDailyEmail } from "@/lib/account-api";
import { formatApiError } from "@/lib/admin-api";
import { useAccount } from "@/lib/account";
import { savedFaith } from "@/lib/useFaith";
import { zodiacVisible, ZODIAC_OFF_BY_DEFAULT, DIET } from "@/lib/faith-customs";

const pill = (on) => `rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${on ? "border-[#9E4429] bg-[#9E4429] text-[#FBF5EE]" : "border-[#C86D51]/25 text-[#5C4A42] hover:border-[#9E4429]"}`;
const Label = ({ children }) => <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#7A665C]">{children}</p>;

const FaithPicker = ({ value, onChange }) => (
  <div>
    <Label>My faith</Label>
    <div className="mt-2 flex flex-wrap gap-1.5">
      {FAITHS.map((f) => <button key={f.id} type="button" onClick={() => onChange(f.id)} data-testid={`profile-faith-${f.id}`} className={pill(value === f.id)}>{f.name}</button>)}
    </div>
    <p className="mt-2 text-xs text-[#6B5A52]" data-testid="profile-diet-note">Meals: {DIET[value].note}</p>
  </div>
);

const SignPicker = ({ value, onChange }) => (
  <div className="mt-3 grid grid-cols-4 gap-1.5 sm:grid-cols-6">
    {ZODIAC.map((z) => (
      <button key={z.id} type="button" onClick={() => onChange(value === z.id ? null : z.id)} data-testid={`profile-sign-${z.id}`} title={z.dates}
        className={`rounded-lg py-2 text-xs font-semibold transition-colors ${value === z.id ? "bg-[#9E4429] text-[#FBF5EE]" : "bg-[#F4E6DC] text-[#5C4A42] hover:bg-[#EBD6C8]"}`}>{z.name}</button>
    ))}
  </div>
);

const BodyPicker = ({ value, onChange }) => (
  <div>
    <Label>My body type</Label>
    <div className="mt-2 grid grid-cols-3 gap-1.5">
      {BODY_TYPES.map((b) => (
        <button key={b.id} type="button" onClick={() => onChange(value === b.id ? null : b.id)} data-testid={`profile-body-${b.id}`}
          className={`rounded-2xl border px-2 py-2.5 text-left transition-colors ${value === b.id ? "border-[#9E4429] bg-[#9E4429] text-[#FBF5EE]" : "border-[#C86D51]/25 text-[#5C4A42] hover:border-[#9E4429]"}`}>
          <span className="block text-sm font-semibold">{b.name}</span>
          <span className="block text-[11px] leading-tight opacity-80">{b.tag}</span>
        </button>
      ))}
    </div>
    {!value && <p className="mt-2 text-xs text-[#6B5A52]">Not sure? Leave it empty and we'll use a balanced plate.</p>}
  </div>
);

const tzNow = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const md = (s) => { const [m, d] = s.trim().split(" "); return (MONTHS.indexOf(m) + 1) * 100 + Number(d); };
// Sign from a yyyy-MM-dd birthday, using the date ranges in ZODIAC (Capricorn wraps the new year)
export const signFromBirthday = (iso) => {
  const [, m, d] = (iso ?? "").split("-").map(Number);
  if (!m || !d) return null;
  const v = m * 100 + d;
  return ZODIAC.find((z) => {
    const [a, b] = z.dates.split("–").map(md);
    return a <= b ? v >= a && v <= b : v >= a || v <= b;
  })?.id ?? null;
};

const BirthdayField = ({ value, onChange, sign }) => (
  <label className="block rounded-2xl border border-[#C86D51]/20 bg-[#FDFBF7] p-4 text-sm font-semibold text-[#2C221E]">My birthday <span className="text-xs font-normal text-[#6B5A52]" data-testid="profile-birthday-note">({sign ? "sets your sign and brings a birthday email" : "brings a birthday email with a blessing and a celebration plate"})</span>
    <input type="date" value={value ?? ""} max={new Date().toISOString().slice(0, 10)} onChange={(e) => onChange(e.target.value || null)} data-testid="profile-birthday-input"
      className="mt-1.5 h-11 w-full rounded-xl border border-[#C86D51]/25 bg-white px-3 text-[#2C221E] outline-none focus:border-[#9E4429]" />
  </label>
);

const EmailToggle = ({ on, onChange, saved, hasBirthday }) => {
  const [sending, setSending] = useState(false);
  const preview = async (kind) => {
    setSending(true);
    try { await previewDailyEmail(kind); toast("Preview on its way", { description: "Check your inbox in a minute." }); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSending(false); }
  };
  return (
    <div className="rounded-2xl border border-[#C86D51]/20 bg-[#FDFBF7] p-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-[#2C221E]">Morning email</p>
          <p className="text-xs text-[#6B5A52]" data-testid="profile-email-note">Your daily word, plate and style at 7:00 am ({tzNow()}).</p>
        </div>
        <Switch checked={on} onCheckedChange={onChange} data-testid="profile-email-switch" aria-label="Morning email" />
      </div>
      {saved && (
        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
          <button type="button" onClick={() => preview("daily")} disabled={sending} data-testid="profile-email-preview" className="text-sm font-semibold text-[#9E4429] underline-offset-2 hover:underline disabled:opacity-60">{sending ? "Sending..." : "Send me a preview now"}</button>
          {hasBirthday && <button type="button" onClick={() => preview("birthday")} disabled={sending} data-testid="profile-birthday-preview" className="text-sm font-semibold text-[#9E4429] underline-offset-2 hover:underline disabled:opacity-60">Preview my birthday email</button>}
        </div>
      )}
      {on && <p className="mt-2 text-xs text-[#6B5A52]">On your birthday you'll get a birthday email instead.</p>}
    </div>
  );
};

export const ProfileDialog = () => {
  const { user, mergeOpen, authMode, profileOpen, setProfileOpen, updateProfile } = useAccount();
  const p = user?.profile ?? {};
  const setup = Boolean(user) && p.setup_done !== true && !mergeOpen && !authMode;
  const open = Boolean(user) && (profileOpen || setup);
  const [faith, setFaith] = useState("spiritual");
  const [zodiac, setZodiac] = useState(null);
  const [show, setShow] = useState(null);
  const [body, setBody] = useState(null);
  const [email, setEmail] = useState(false);
  const [birthday, setBirthday] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFaith(p.faith ?? savedFaith());
    setZodiac(p.zodiac ?? null);
    setShow(p.show_zodiac ?? null);
    setBody(p.body_type ?? null);
    setEmail(Boolean(p.daily_email));
    setBirthday(p.birthday ?? null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const visible = zodiacVisible({ faith, show_zodiac: show });
  const save = async (skip) => {
    setBusy(true);
    try {
      await updateProfile(skip ? { faith: p.faith ?? savedFaith(), setup_done: true } : { faith, zodiac, show_zodiac: show, body_type: body, daily_email: email, timezone: tzNow(), ...(birthday ? { birthday } : {}), setup_done: true });
      if (!skip) toast("Profile saved", { description: "Your daily word, meals and style now follow you on every device." });
      setProfileOpen(false);
    } catch {
      toast.error("Couldn't save your profile. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { if (setup) save(true); else setProfileOpen(false); } }}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto rounded-3xl border-[#C86D51]/20 bg-[#FBF5EE] p-7" data-testid="profile-dialog">
        <DialogHeader className="text-left">
          <DialogTitle className="font-serif text-3xl font-light text-[#2C221E]" data-testid="profile-dialog-title">{setup ? "Make Lunelle yours" : "My profile"}</DialogTitle>
          <DialogDescription className="text-[#5C4A42]">Your faith shapes your daily word, meals, fasting seasons and wording. Saved to your account, so it follows you everywhere.</DialogDescription>
        </DialogHeader>
        <FaithPicker value={faith} onChange={setFaith} />
        <BodyPicker value={body} onChange={setBody} />
        <BirthdayField value={birthday} sign={visible} onChange={(v) => { setBirthday(v); const s = signFromBirthday(v); if (s) setZodiac(s); }} />
        <div className="rounded-2xl border border-[#C86D51]/20 bg-[#FDFBF7] p-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-[#2C221E]">Show zodiac & sign styling</p>
              <p className="text-xs text-[#6B5A52]">{ZODIAC_OFF_BY_DEFAULT.includes(faith) && show === null ? "Off by default for your faith. Turn on if you'd like it." : "Optional. Turn off anytime."}</p>
            </div>
            <Switch checked={visible} onCheckedChange={setShow} data-testid="profile-zodiac-switch" aria-label="Show zodiac" />
          </div>
          {visible && (
            <>
              <p className="mt-4 text-sm text-[#5C4A42]">My sign {zodiac ? "" : "(optional)"}</p>
              <SignPicker value={zodiac} onChange={setZodiac} />
            </>
          )}
        </div>
        <EmailToggle on={email} onChange={setEmail} saved={Boolean(p.daily_email)} hasBirthday={Boolean(p.birthday)} />
        <div className="flex flex-wrap items-center justify-end gap-3">
          {setup && <button type="button" onClick={() => save(true)} disabled={busy} data-testid="profile-skip-button" className="text-sm font-medium text-[#5C4A42] hover:text-[#2C221E]">Skip for now</button>}
          <button type="button" onClick={() => save(false)} disabled={busy} data-testid="profile-save-button"
            className="flex h-11 items-center gap-2 rounded-full bg-[#2C221E] px-6 text-sm font-semibold text-[#FBF5EE] transition-colors hover:bg-[#9E4429] disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}Save profile
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
